import { startOfDay, endOfWeek, endOfMonth, subWeeks, subMonths, isAfter } from 'date-fns'
import { t } from 'i18next'
import { useMemo } from 'react'

import { useGetQuartersQuery, useGetTopGamesWithAppIdsQuery, useGetTopGrossingGameQuery, useGetTopGrossingGamesQuery } from '../../../api/top-grossing'
import { average } from '../../../helpers/average'
import { RoleEnum } from '../../account'
import { useRoleCheck } from '../../account/hooks/roleHooks'
import { useSubgenresAccessCheck } from '../../account/hooks/roleHooks'
import type { Game } from '../../game'
import { useResolvedGameNames } from '../../game/hooks/useResolvedGameNames'
import { overlayTopGameNames } from '../../game/util/overlayResolvedGameNames'
import { compareMarketShareScopeTypeFilterNames } from '../../market-share/helpers/scopeTypeFilterSort'
import { MarketShareNumberType, MarketShareNumberTypeId } from '../../market-share/types/MarketShareNumberType'
import { MarketShareScopeType } from '../../market-share/types/MarketShareScopeType'
import { MarketShareScopeTypeFilter, MarketShareScopeTypeFilterAll } from '../../market-share/types/MarketShareScopeTypeFilter'
import { useCurrentMarket } from '../../markets'
import quarterService, { GameDataValueId, GameDataValueName } from '../../quarter/services/QuarterService'
import { getTimestampByGranularity } from '../../revenue-and-downloads/helpers/helpers'
import { GranularityValue } from '../../revenue-and-downloads/types/Filters'
import { useIsGameOpenForEveryone } from '../../settings'
import { PerformanceRank } from '../../update-history/types/types'
import { TopGame } from '../types/TopGame'

export function useTopGrossingGames(marketIso: string, includeArray: string[], limit: number, enableCache: boolean = true) {
  const include = includeArray.join(',')
  const { data: topGames, error, isLoading, isFetching } = useGetTopGrossingGamesQuery({ marketIso, include, limit, enableCache })
  const gameIds = useMemo(() => (topGames || []).map((topGame) => topGame.gameId || topGame.id), [topGames])
  const { namesByGameId, isLoading: isLoadingNames } = useResolvedGameNames(gameIds, { skip: isLoading || isFetching })
  const waitingForNames = Boolean(topGames?.length) && isLoadingNames
  const topGamesWithUserLanguageNames = useMemo(() => overlayTopGameNames(topGames, namesByGameId), [topGames, namesByGameId])

  return {
    data: isLoading || isFetching || waitingForNames ? [] : topGamesWithUserLanguageNames,
    isLoading: isLoading || isFetching || waitingForNames,
    error,
  }
}

/**
 * Hook that loads rank data for a given app, filters data with optional date range
 */

type UseGameUpdateRankDataHookParams = {
  appId: number
  marketIso: string
  granularity?: GranularityValue
}

export const useTopGrossingGameRanks = ({ appId, marketIso, granularity = GranularityValue.Day }: UseGameUpdateRankDataHookParams) => {
  const { data: topGame, isLoading, isFetching, error } = useGetTopGrossingGameQuery({ appId, marketIso })

  const topGrossingRanks: PerformanceRank[] = useMemo(() => {
    return resolveRanks(topGame?.history || [])
  }, [topGame])

  const freeRanks: PerformanceRank[] = useMemo(() => {
    return resolveRanks(topGame?.dhistory || [])
  }, [topGame])

  // Group by granularity, with filtering for completed periods
  const [groupedTopGrossingRanks, groupedFreeRanks] = useTopGrossingGameRanksByGranularity({
    ranks: [topGrossingRanks, freeRanks],
    granularity,
  })
  return { topGrossingRanks: groupedTopGrossingRanks, freeRanks: groupedFreeRanks, isLoading: isLoading || isFetching, error }
}

/**
 * Hook that groups ranks by granularity and calculates average rank for each granularity group, with filtering for last completed period
 */
const useTopGrossingGameRanksByGranularity = ({
  ranks,
  granularity = GranularityValue.Day,
}: {
  ranks: [topGrossingRanks: PerformanceRank[], freeRanks: PerformanceRank[]]
  granularity?: GranularityValue
}) => {
  const now = new Date()
  let lastCompletedPeriodEnd: Date

  if (granularity === GranularityValue.Week) {
    lastCompletedPeriodEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }) // Last completed week
  } else if (granularity === GranularityValue.Month) {
    lastCompletedPeriodEnd = endOfMonth(subMonths(now, 1)) // Last completed month
  } else {
    lastCompletedPeriodEnd = now // Use the current date for daily granularity
  }

  return useMemo(() => {
    return ranks.map((ranks) => {
      const ranksGroupedByGranularity = ranks.reduce((acc, rank) => {
        const granularityParsedDate = new Date(rank.ts)
        const parsedDate = getTimestampByGranularity(granularity, granularityParsedDate).getTime()

        // Ensure only completed periods are included
        if (isAfter(lastCompletedPeriodEnd, new Date(parsedDate))) {
          const dateFormattedByGranularity = parsedDate
          const current = acc[dateFormattedByGranularity]

          if (!current) {
            acc[dateFormattedByGranularity] = [{ ...rank, ts: dateFormattedByGranularity }]
          } else {
            acc[dateFormattedByGranularity] = [...current, { ...rank, ts: dateFormattedByGranularity }]
          }
        }

        return acc
      }, {} as { [key: number]: PerformanceRank[] })

      const rankAverages = Object.entries(ranksGroupedByGranularity).map(([date, ranks]) => {
        const averageValue = average(ranks.map((rank) => rank.rank))
        const ts = +date
        return { rank: averageValue, ts }
      })

      return resolveRanks(Object.values(rankAverages))
    }) as [groupedTopGrossingRanks: PerformanceRank[], groupedFreeRanks: PerformanceRank[]]
  }, [granularity, ranks, lastCompletedPeriodEnd])
}

const resolveRanks = (ranks: PerformanceRank[]) => {
  return [...ranks].sort((a, b) => a.ts - b.ts).map((rank) => ({ ...rank, ts: startOfDay(rank.ts).getTime() }))
}

export const useFilteredQuarters = () => {
  const { currentMarketIso: mainMarketIso } = useCurrentMarket()
  const { data: quarters, isFetching: isFetchingQuarters } = useGetQuartersQuery({ marketIso: mainMarketIso })

  const marketShareUnlocked = useRoleCheck(RoleEnum.market_share)
  const filteredQuarters = useMemo(() => {
    if (!quarters) {
      return
    }

    if (marketShareUnlocked) {
      return quarters
    }
    return [quarters[0], quarters[1]]
  }, [quarters, marketShareUnlocked])
  return { filteredQuarters, isLoading: isFetchingQuarters }
}

type UseGetTopGamesOptions = {
  // When true, the fetched appId set also includes the games whose recomputed
  // (revenuesAndDownloads-based) Top 500 by revenue / downloads qualifies them — not only the
  // API `grossingGames` / `freeGames` lists. The Market Share Top 500 Games table derives
  // membership and the displayed `%` from the recomputed values, so every recomputed Top 500
  // game must be fetched, otherwise the list can render fewer than 500 rows. Off by default to
  // avoid widening the dataset for other consumers (e.g. Genre Essentials). (GR-2878)
  includeRecomputedTop500Ids?: boolean
}

export const useGetTopGames = (topGamesInclude: string[], { includeRecomputedTop500Ids = false }: UseGetTopGamesOptions = {}) => {
  const { currentMarketIso: mainMarketIso } = useCurrentMarket()
  const { filteredQuarters, isLoading: isFetchingQuarters } = useFilteredQuarters()

  const uniqueAppIds = useMemo(() => {
    if (!filteredQuarters) {
      return []
    }

    const appIds: number[] = []
    const recomputedTop500PerQuarter = includeRecomputedTop500Ids ? quarterService.top500GamesPerQuarter(filteredQuarters) : undefined
    const pushAppId = (appId: number) => {
      if (Number.isInteger(appId) && appId > 0) {
        appIds.push(appId)
      }
    }

    filteredQuarters.forEach((quarter) => {
      quarter.grossingGames.forEach(pushAppId)
      quarter.freeGames.forEach(pushAppId)
      pushAppId(1544884479)
      const recomputedTop500 = recomputedTop500PerQuarter?.[quarter.quarterIdentifier]
      if (recomputedTop500) {
        recomputedTop500.revenue.forEach((gameId: string) => pushAppId(Number(gameId)))
        recomputedTop500.downloads.forEach((gameId: string) => pushAppId(Number(gameId)))
      }
    })

    return [...new Set(appIds)]
  }, [filteredQuarters, includeRecomputedTop500Ids])

  const { data: topGames, isFetching: isFetchingTopGames } = useGetTopGamesWithAppIdsQuery(
    { marketIso: mainMarketIso, appIds: uniqueAppIds, include: topGamesInclude.join(',') },
    { skip: uniqueAppIds.length === 0 }
  )
  const gameIds = useMemo(() => (topGames || []).map((topGame) => topGame.gameId || topGame.id), [topGames])
  const { namesByGameId, isLoading: isLoadingNames } = useResolvedGameNames(gameIds, { skip: isFetchingTopGames || !topGames?.length })
  const waitingForNames = Boolean(topGames?.length) && isLoadingNames
  const topGamesWithUserLanguageNames = useMemo(() => overlayTopGameNames(topGames, namesByGameId), [topGames, namesByGameId])

  return { topGames: topGamesWithUserLanguageNames, isLoading: isFetchingQuarters || isFetchingTopGames || waitingForNames }
}

export const marketShareScopeTypes: MarketShareScopeType[] = [
  {
    name: 'common:conventional_subgenre',
    id: 'subgenre',
    gameDataValueId: GameDataValueId.ConventionalSubgenreId,
    gameDataValueName: GameDataValueName.ConventionalSubgenre,
    allLocalizationKey: 'common:all_subgenres',
  },
  {
    name: 'common:conventional_genre',
    id: 'genre',
    gameDataValueId: GameDataValueId.ConventionalGenreId,
    gameDataValueName: GameDataValueName.ConventionalGenre,
    allLocalizationKey: 'common:all_conventional_genres',
  },
  {
    name: 'common:conventional_category',
    id: 'category',
    gameDataValueId: GameDataValueId.ConventionalCategoryId,
    gameDataValueName: GameDataValueName.ConventionalCategory,
    allLocalizationKey: 'common:all_conventional_categories',
  },
  {
    name: 'common:publisher',
    id: 'publisher',
    gameDataValueId: GameDataValueId.Artist,
    gameDataValueName: GameDataValueName.Artist,
    allLocalizationKey: 'common:all_publishers',
  },
]
export const marketShareNumberTypes: MarketShareNumberType[] = [
  {
    name: 'market-share:number_type_percentages_name',
    id: MarketShareNumberTypeId.Percentage,
  },
  {
    name: 'market-share:number_type_value_name',
    id: MarketShareNumberTypeId.Value,
  },
]

export const useGetMarketShareData = (marketShareScopeType: MarketShareScopeType, topGames?: TopGame[], isFetchingTopGames?: boolean) => {
  const { filteredQuarters, isLoading: isFetchingQuarters } = useFilteredQuarters()

  const marketShareData = useMemo(() => {
    if (topGames && filteredQuarters && !isFetchingTopGames && !isFetchingQuarters) {
      const { topGamesWithGameQuarters, calculatedQuarters } = quarterService.getAndCalculateGameQuarters(topGames, filteredQuarters)
      return {
        topGamesWithGameQuarters,
        calculatedQuarters,
        marketShares: quarterService.getAndCalculateMarketShares(
          topGamesWithGameQuarters,
          calculatedQuarters,
          marketShareScopeType.gameDataValueId,
          marketShareScopeType.gameDataValueName
        ),
      }
    } else {
      return null
    }
  }, [topGames, filteredQuarters, marketShareScopeType, isFetchingTopGames, isFetchingQuarters])

  return { marketShareData, isLoading: isFetchingQuarters }
}

export const useGetMarketShareScopeTypeFilters = (topGames: TopGame[], marketShareScopeType: MarketShareScopeType, isFetchingTopGame?: boolean) => {
  const { marketShareData } = useGetMarketShareData(marketShareScopeType, topGames, isFetchingTopGame)
  const marketShareScopeTypeFilters = useMemo(() => {
    if (marketShareData) {
      const filtersMap: { [key: string]: string } = {}

      marketShareData.topGamesWithGameQuarters.forEach((game) => {
        const gameDataValue = game[marketShareScopeType.gameDataValueId]
        if (gameDataValue && !filtersMap[gameDataValue]) {
          filtersMap[gameDataValue] = game[marketShareScopeType.gameDataValueName]
        }
      })

      const filters: MarketShareScopeTypeFilter[] = Object.keys(filtersMap).map((key) => {
        return { id: key, name: filtersMap[key] }
      })

      filters.sort(compareMarketShareScopeTypeFilterNames)
      filters.unshift({ id: MarketShareScopeTypeFilterAll, name: t(marketShareScopeType.allLocalizationKey) })

      return filters
    } else {
      return null
    }
  }, [marketShareData, marketShareScopeType])

  return { marketShareScopeTypeFilters, isLoading: isFetchingTopGame }
}

export const useShowSubgenreForGame = (game: TopGame | Game): boolean => {
  const hasAccessToSubgenres = useSubgenresAccessCheck()
  const isGameUnlockedForEveryone = useIsGameOpenForEveryone(game)

  if (!hasAccessToSubgenres) {
    if (isGameUnlockedForEveryone) {
      return true
    }

    if (game.rank <= 10) {
      return true
    }

    return false
  }

  return true
}
